import { useEffect } from 'react';
import Footer from '../../home/components/Footer';

export default function BathroomRenovationPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://americandreamremodelinc.com';

    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Bathroom Renovation',
      description: 'Expert bathroom renovation in Southeast Michigan — shower & tub installation, custom vanities, tile work, heated floors, and plumbing updates by American Dream Remodeling INC.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'American Dream Remodeling INC',
        url: siteUrl,
        telephone: '+1-586-443-0294',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Detroit',
          addressRegion: 'MI',
          addressCountry: 'US',
        },
      },
      areaServed: [
        { '@type': 'County', name: 'Macomb County' },
        { '@type': 'County', name: 'Oakland County' },
        { '@type': 'County', name: 'Wayne County' },
        { '@type': 'County', name: 'Washtenaw County' },
      ],
      url: `${siteUrl}/services/bathroom-renovation`,
    };

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/#services` },
        { '@type': 'ListItem', position: 3, name: 'Bathroom Renovation', item: `${siteUrl}/services/bathroom-renovation` },
      ],
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much does a bathroom renovation cost in Southeast Michigan?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Bathroom renovation costs depend on the size of the space, materials chosen, and scope of work. American Dream Remodeling INC provides free, no-obligation estimates for all bathroom renovation projects across Macomb, Oakland, Wayne, and Washtenaw County.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long does a bathroom renovation take?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most bathroom renovations are completed within 1–2 weeks. Larger master bath projects may take 2–4 weeks. We provide a detailed timeline during your free consultation.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do you install heated floors in bathrooms?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, American Dream Remodeling INC installs radiant heated floor systems as part of our bathroom renovation services throughout Southeast Michigan.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can you update plumbing during a bathroom renovation?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolutely. We handle full plumbing updates including fixture replacement, pipe relocation, and installation of modern efficient systems as part of our bathroom renovation service.',
          },
        },
      ],
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Bathroom Renovation in Southeast Michigan | American Dream Remodeling INC',
      description: 'Expert bathroom renovation in Southeast Michigan — shower & tub installation, custom vanities, tile work, heated floors, and plumbing updates by American Dream Remodeling INC.',
      url: `${siteUrl}/services/bathroom-renovation`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/#services` },
          { '@type': 'ListItem', position: 3, name: 'Bathroom Renovation', item: `${siteUrl}/services/bathroom-renovation` },
        ],
      },
    };

    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        (el as HTMLScriptElement).type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };

    injectSchema('schema-bathroom-service', serviceSchema);
    injectSchema('schema-bathroom-breadcrumb', breadcrumb);
    injectSchema('schema-bathroom-faq', faqSchema);
    injectSchema('schema-bathroom-webpage', webPageSchema);

    return () => {
      ['schema-bathroom-service', 'schema-bathroom-breadcrumb', 'schema-bathroom-faq', 'schema-bathroom-webpage'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  const scrollToEstimate = () => {
    sessionStorage.setItem('scrollToEstimate', 'true');
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/efb36e20a1e9c6064b0ed7242b17a142.jpg"
            alt="Bathroom Renovation Services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Bathroom Renovation</h1>
          <p className="text-xl md:text-2xl mb-8">Create your dream bathroom with luxurious fixtures, elegant tile work, and smart storage solutions that maximize space.</p>
          <button
            onClick={scrollToEstimate}
            className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-600 transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Get Free Estimate
          </button>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Transform Your Bathroom</h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                A bathroom renovation is one of the best investments you can make in your home. Whether you want a spa-like retreat, improved functionality, or modern updates, we bring your vision to life with expert craftsmanship and attention to detail.
              </p>
              <p className="text-gray-600 leading-relaxed">
                From small powder rooms to master bath suites, American Dream Remodeling INC has the experience and expertise to handle projects of any size throughout Southeast Michigan.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=modern%20bathroom%20vanity%20with%20double%20sinks%20large%20mirror%20elegant%20lighting%20white%20marble%20countertop%20clean%20simple%20background%20professional%20interior%20photography&width=800&height=600&seq=bathroom-detail-001&orientation=landscape"
                alt="Custom Bathroom Vanity"
                className="w-full h-full object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>

          {/* What We Offer */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What We Offer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: 'ri-drop-line', title: 'Shower & Tub Installation', desc: 'Walk-in showers, soaking tubs, and more' },
                { icon: 'ri-layout-masonry-line', title: 'Tile Work', desc: 'Beautiful ceramic, porcelain, and natural stone' },
                { icon: 'ri-archive-drawer-line', title: 'Custom Vanities', desc: 'Storage solutions that fit your space' },
                { icon: 'ri-lightbulb-flash-line', title: 'Lighting & Ventilation', desc: 'Proper lighting and moisture control' },
                { icon: 'ri-water-flash-line', title: 'Plumbing Updates', desc: 'Modern fixtures and efficient systems' },
                { icon: 'ri-temp-hot-line', title: 'Heated Floors', desc: 'Luxury radiant floor heating' },
              ].map((item, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-500 text-white rounded-lg mb-4">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Process */}
          <div className="bg-gradient-to-br from-gray-50 to-white p-12 rounded-2xl border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: '1', title: 'Free Consultation', desc: 'Discuss your needs and budget' },
                { step: '2', title: 'Design & Planning', desc: 'Custom design tailored to your space' },
                { step: '3', title: 'Professional Installation', desc: 'Expert craftsmanship every step' },
                { step: '4', title: 'Final Reveal', desc: 'Your beautiful new bathroom' },
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-500 text-white rounded-full text-2xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready for Your Dream Bathroom?</h2>
          <p className="text-xl text-white/90 mb-8">Get a free, no-obligation estimate for your bathroom renovation project in Southeast Michigan.</p>
          <button
            onClick={scrollToEstimate}
            className="bg-white text-teal-600 px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Get Your Free Estimate
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}